import LinearProgress from "@mui/material/LinearProgress";
import { ButtonIcon, CSS_DISABLE, CSS_SELECTED, CSS_SHOW } from "designSystem/components/editor/config";
import { MARK_IMPORTANT_BUTTON, MAX_HEIGHT_OF_TOOLBAR, MIN_HEIGHT_OF_TOOLBAR, openPopupsInRightPane, PLACEHOLDER_HEIGHT, PRE_SEND_BUTTON, SEND_BUTTON, TOOLBAR_BUTTONS } from "designSystem/components/editor/configForRightPane";
import FroalaEditorComponent, { appendSignatureAndMoveCursorToStart, disableEditorToolbar, enableEditorToolbar, getEditorContent, getFroalaContainer, getFroalaEditor, LINK_CONTEXT_ID, setEditorContent } from "designSystem/components/editor/FroalaEditorComponent";
import { Box, FlexRowCenter, Text } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import { useStore } from "csi/state/hooks";
import { RegisterCommand, RegisterShortcut } from "froala-editor";
import React, { FunctionComponent, MutableRefObject, useEffect, useRef, useState } from "react";
import FroalaEditorType from "react-froala-wysiwyg";

const CSS_FOR_IMPORTANT = "custom-important";
const CSS_FOR_BUTTON = `${CSS_SELECTED} ${CSS_FOR_IMPORTANT}`;
const SHORTCUT_ENTER = "post-message";
const SPECIAL_KEYS = ["ArrowLeft", "ArrowRight", "ArrowDown", "ArrowUp", "Backspace", "CapsLock", "Enter", "Escape", "Shift", "Tab"];
const SHORTCUT_STAR = "*";
const SHORTCUT_DOT = ".";
const SHORTCUT_ONE = "1";
const CUSTOM_LAST_INPUT = "lastInput";

/** get url to unfurl  */
function getUrlToUnfurl(html: string): string {
	const el = document.createElement("html");
	el.innerHTML = html;
	const nodeList: HTMLCollectionOf<HTMLAnchorElement> = el.getElementsByTagName("a");

	/** only support one url for now */
	const firstLink = nodeList[0];
	const inputUrlStr = firstLink?.href;

	// exclude the one attached as the context
	if (inputUrlStr && !(firstLink.attributes[LINK_CONTEXT_ID])) {
		const webUrl = new URL(location.toString());
		const inputUrl = new URL(inputUrlStr);

		if (inputUrl.hostname === webUrl.hostname) {
			return inputUrlStr;
		}
	}

	return "";
}

interface ChatEditorProps {
	/** append signature onto message with link to node */
	appendSignature?: boolean;

	/** indicate if enable mark important button */
	enableMarkImportant?: boolean;

	/** callback for onblur event */
	onBlur?: VoidFunction;

	/** callback for onInit event */
	onInit?: (ref: MutableRefObject<FroalaEditorType>) => void;

	/** callback for send event */
	onSend?: (content: string, urlToUnfurl?: string) => Promise<boolean>;

	/** callback for set importance */
	onSetImportance?: (important: boolean) => void;

	/** placeholder for editor */
	placeholderRes?: string;
}

const ChatEditor: FunctionComponent<ChatEditorProps> = (props) => {
	const { appendSignature, enableMarkImportant, onBlur, onInit, onSend, placeholderRes, onSetImportance } = props;
	const buttons = {
		...TOOLBAR_BUTTONS,
		moreMisc: {
			buttons: ["bold", "italic", "underline", "strikeThrough",
				"emoticons", "formatUL", "formatOLSimple", "backgroundColor", "textColor", "fontSize", "paragraphFormat", "indent", "outdent", "clearFormatting",
				"insertLink", "quote", "insertHR", "insertTable", enableMarkImportant ? MARK_IMPORTANT_BUTTON : "", "undo", "redo"],
			buttonsVisible: 4,
		},
	};
	const res = placeholderRes ?? "PostChannelMessage";
	const ref = useRef<FroalaEditorType>();
	const loc = useLocale();
	const { routerStore } = useStore();
	const [readyToChat, setReadyToChat] = useState<boolean>(false);
	const [important, setImportant] = useState<boolean>(false);
	const [posting, setPosting] = useState<boolean>(false);

	/** append signature to text and move the cursor to the beginning */
	const _appendSignatureAndMoveCursorToStart = () => {
		appendSignatureAndMoveCursorToStart(ref.current, loc.resource("SentFrom"), routerStore.locationName, location.href);
	};

	/** start chat */
	const startChat = () => setReadyToChat(true);

	// handler the popups to avoid mess up
	const onOpenPopups = (a) => openPopupsInRightPane(ref.current, a);

	// get the content of the editor
	const getContent = (): string => getEditorContent(ref.current);

	/** adjust css if support marking message important */
	const adjustCSS = (): void => {
		if (ref.current) {
			const contentContainer = getFroalaContainer(ref.current);
			const parentDiv = contentContainer.parentElement;

			if (important) {
				contentContainer.classList.add(CSS_FOR_IMPORTANT);
				const newNode = document.createElement("span");
				newNode.innerText = loc.resource("Important");
				newNode.className = CSS_FOR_IMPORTANT;
				parentDiv.insertBefore(newNode, contentContainer);
			} else {
				contentContainer.classList.remove(CSS_FOR_IMPORTANT);
				parentDiv.removeChild(parentDiv.firstChild);
			}
		}
	};

	if (enableMarkImportant) {
		RegisterCommand(MARK_IMPORTANT_BUTTON, {
			callback: async () => {
				// callback
				setImportant(!important);
				onSetImportance?.(!important);
			},
			focus: false,
			icon: ButtonIcon.markImportant,
			refresh: (btn) => {
				if (important) {
					btn.addClass(CSS_FOR_BUTTON);
				} else {
					btn.removeClass(CSS_FOR_BUTTON);
				}
			},
			undo: false,
		});
	}

	RegisterCommand(PRE_SEND_BUTTON, {
		focus: false,
		icon: ButtonIcon.preSend,
		refresh: (btn) => {
			if (!getContent()) {
				btn.addClass(CSS_SHOW + " " + CSS_DISABLE);
			} else {
				btn.removeClass(CSS_SHOW + " " + CSS_DISABLE);
			}
		},
		undo: false,
	});

	/** send messages */
	const sendMessage = async (): Promise<void> => {
		const content = getContent();
		const text = content.trim();
		const editor = getFroalaEditor(ref.current);

		if (text) {
			try {
				// clear out the content first, otherwise, kind of feeling like it fails to send out due to async delay
				setEditorContent(ref.current, "");

				if (appendSignature) {
					_appendSignatureAndMoveCursorToStart();
				}

				disableEditorToolbar(ref.current);
				editor.edit.off();
				setPosting(true);
				const url = getUrlToUnfurl(content);
				const result = await onSend?.(text, url);

				if (result) {
					setImportant(false);
				}
			} catch (error) {
				console.error(error.message);
			} finally {
				enableEditorToolbar(ref.current);
				editor.edit.on();
				editor.undo.reset();
				setPosting(false);
				editor[CUSTOM_LAST_INPUT] = [];
			}
		}
	};

	RegisterCommand(SEND_BUTTON, {
		callback: async () => {
			await sendMessage();
		},
		focus: false,
		icon: ButtonIcon.send,
		refresh: (btn) => {
			if (!getContent()) {
				btn.removeClass(CSS_SHOW);
			} else {
				btn.addClass(CSS_SHOW);
			}
		},
		undo: false,
	});

	// register shortcut for Enter key
	RegisterShortcut(13, SHORTCUT_ENTER, SHORTCUT_ENTER);

	/** callback for shortcuts  */
	const callbackOnShortcut = async (e, cmd, val): Promise<void> => {
		if (cmd === SHORTCUT_ENTER) {
			await sendMessage();
			const editor = getFroalaEditor(ref.current);
			editor.events.focus();
		}
	};

	// when blur, disable the toolbar
	const _onBlur = (object, e) => {
		disableEditorToolbar(ref.current);

		if (!getContent()) {
			setReadyToChat(false);
			setImportant(false);
		}

		onBlur?.();
	};

	const _onInit = () => {
		if (appendSignature) {
			_appendSignatureAndMoveCursorToStart();
		}

		onInit?.(ref);
	};

	/**
	 * add the ability to convert the special patterns to bullet/order list, already report this to Froala, 
	 * they'll made these features available in the future release - https://wysiwyg-editor-roadmap.froala.com/boards/feature-requests/3553
	 * for now we do this to workaround
	 */
	const _onKeydown = (keyObj: KeyboardEvent): void => {
		const editor = getFroalaEditor(ref.current);
		const { cursor, lists } = editor;
		const { altKey, ctrlKey, key, metaKey } = keyObj;

		const lastInputStack = editor[CUSTOM_LAST_INPUT];
		const lastInput = lastInputStack?.[lastInputStack.length - 1];
		const lastInput2 = lastInputStack?.[lastInputStack.length - 2];

		// if user type in SPACE
		if (key === " ") {
			if (lastInput === SHORTCUT_STAR) {
				lists.format("UL");

				// after the command above, will introduce "*" and "SPACE", so prevent one and delete one
				keyObj.preventDefault();
				cursor.backspace();
			} else if (lastInput === SHORTCUT_DOT && lastInput2 === SHORTCUT_ONE) {
				lists.format("OL");

				// after the command above, will introduce "1", ".", and "SPACE", so prevent one and delete two
				keyObj.preventDefault();
				cursor.backspace();
				cursor.backspace();
			}
		}

		if (!altKey && !ctrlKey && !metaKey && !(SPECIAL_KEYS.includes(key))) {
			editor[CUSTOM_LAST_INPUT] ||= [];
			editor[CUSTOM_LAST_INPUT].push(key);
		}
	};

	useEffect(() => {
		if (enableMarkImportant) {
			adjustCSS();
		}
	}, [important]);

	return (
		<>
			{readyToChat ? (
				<>
					{posting && (
						<Box p={0.25}>
							<LinearProgress />
						</Box>
					)}
					<FroalaEditorComponent
						configProps={{
							editorClass: "comment-reply",
							events: {
								blur: _onBlur,
								"commands.after": onOpenPopups,
								/**
								 * C8-54796
								 * Do not cleanup / sanitize pasted content.
								 * We already implemented this -> FroalaEditorComponent#getEditorContent
								 */
								"paste.beforeCleanup": () => false,
								initialized: _onInit,
								shortcut: callbackOnShortcut,
								keydown: _onKeydown,
							},
							// after inserting other elements, would make the placeholder jitter, given we already put an additional placeholder below, avoid show up here
							placeholderText: enableMarkImportant ? "" : loc.resource(res),
							toolbarButtons: buttons,
							fontSize: ["10", "12", "14"],
							linkEditButtons: ["linkOpen", "linkEdit", "linkRemove"],
						}}
						heightMax={MAX_HEIGHT_OF_TOOLBAR}
						heightMin={MIN_HEIGHT_OF_TOOLBAR}
						ref={ref}
						titleRes={res}
						value=""
					/>
				</>
			) : (
				<FlexRowCenter
					borderBottom={STANDARD_BORDER_STYLE}
					borderTop={STANDARD_BORDER_STYLE}
					color="grey.800"
					flexShrink={0}
					height={PLACEHOLDER_HEIGHT}
					onClick={startChat}
					p={4}
				>
					<Text res={res} style={{ marginBottom: 1 }} />
				</FlexRowCenter>
			)}
		</>
	);
};

export default ChatEditor;